import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRightLeft } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useCurrency } from "@/context/CurrencyContext";
import { arDigits } from "@/lib/utils";
import { fetchAreas, resolvePair, PAIR_EVENT } from "@/lib/marketAreas";
import RoundSelect from "@/components/market/intel/RoundSelect";
import { CT, nm, MetricRows, TrendCompare, SalesCompare, BandsCompare, MovementCompare } from "./compare/CompareCharts";
import { useIntelCurrency } from "@/components/market/intel/intel";

const toSlug = (name) => (name || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
const findAreaBySlug = (areas, slug) => (slug && areas?.length ? areas.find((a) => toSlug(a.area) === slug.toLowerCase()) || null : null);

function PairSeo({ A, B, lang }) {
  const pairSlug = `${toSlug(A.area)}-vs-${toSlug(B.area)}`;
  const enUrl = `https://thepropertic.ae/dubai-compare-areas/${pairSlug}`;
  const arUrl = `${enUrl}?lang=ar`;
  const canonical = lang === "ar" ? arUrl : enUrl;
  const nameA = lang === "ar" ? A.area_ar || A.area : A.area;
  const nameB = lang === "ar" ? B.area_ar || B.area : B.area;
  const compareLd = {
    "@context": "https://schema.org",
    "@graph": [
      { "@type": "WebPage", "@id": `${enUrl}#page`, "url": canonical,
        "name": lang === "ar" ? `مقارنة عقارية بين ${nameA} و${nameB} · بروبيرتك` : `Compare ${nameA} vs ${nameB} · Dubai communities · The Propertic`,
        "inLanguage": lang === "ar" ? "ar" : "en", "isPartOf": { "@id": "https://thepropertic.ae/#website" },
        "about": [{ "@type": "Place", "name": A.area }, { "@type": "Place", "name": B.area }] },
      { "@type": "BreadcrumbList", "itemListElement": [
        { "@type": "ListItem", position: 1, name: lang === "ar" ? "الرئيسية" : "Home", item: "https://thepropertic.ae/" },
        { "@type": "ListItem", position: 2, name: lang === "ar" ? "قارن المناطق" : "Compare areas", item: "https://thepropertic.ae/dubai-compare-areas" },
        { "@type": "ListItem", position: 3, name: `${nameA} vs ${nameB}`, item: canonical },
      ] },
    ],
  };
  const title = lang === "ar" ? `مقارنة ${nameA} و${nameB} · عقارات دبي · بروبيرتك` : `${nameA} vs ${nameB} · Compare Dubai communities · The Propertic`;
  const description = lang === "ar"
    ? `قارن بين ${nameA} و${nameB} في دبي: متوسط سعر القدم²، عائد الإيجار، صفقات ٢٠٢٦، ونسبة قيد الإنشاء مقابل الجاهز. مصدر: دائرة الأراضي والأملاك.`
    : `Compare ${nameA} vs ${nameB} in Dubai side-by-side: AED/sqft, gross rental yield, YTD 2026 transactions, off-plan vs ready split. Source: Dubai Land Department.`;
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="ar" href={arUrl} />
      <link rel="alternate" hrefLang="x-default" href={enUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content="https://thepropertic.ae/og-share-logo.png" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <script type="application/ld+json">{JSON.stringify(compareLd)}</script>
    </Helmet>
  );
}

function Header({ isAR }) {
  return (
    <div className="max-w-4xl mb-5 sm:mb-6">
      <p className="font-mono-brand text-propertic-gold mb-2 text-[10px] tracking-[0.24em] uppercase">{isAR ? "مقارنة" : "Compare"}</p>
      <h2 className="font-editorial text-2xl sm:text-3xl leading-[1.15] text-propertic-navy">
        {isAR ? <>مقارنة بين <span className="font-italic-serif text-propertic-gold">منطقتين.</span></> : <>Two areas · <span className="font-italic-serif text-propertic-gold">side by side.</span></>}
      </h2>
      <p className="mt-2 text-propertic-ink-2/95 text-[13px] sm:text-[14px] leading-relaxed">
        {isAR
          ? "قارن بين أي منطقتين من مناطق التملّك الحرّ في دبي استناداً إلى المصدر الرسمي نفسه: بوابة البيانات المفتوحة لدائرة الأراضي والأملاك، مع تحديث أسبوعي."
          : "Benchmark any two Dubai freehold communities using the same official source: Dubai Land Department Open Data Gateway, refreshed weekly."}
      </p>
    </div>
  );
}

export default function AreaCompare() {
  const { lang } = useLang();
  const isAR = lang === "ar";
  const { format } = useCurrency();
  useIntelCurrency();
  const { pair: urlPair } = useParams();
  const [areas, setAreas] = useState([]);
  const [aSlug, setASlug] = useState("Jumeirah Village Circle");
  const [bSlug, setBSlug] = useState("Jumeirah Beach Residence");
  const [activity, setActivity] = useState(null);
  const [loadErr, setLoadErr] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    setLoadErr(false);
    fetchAreas().then((list) => {
      setAreas(list);
      if (list.length && !list.find((x) => x.area === aSlug)) setASlug(list[0].area);
      if (list.length && !list.find((x) => x.area === bSlug)) setBSlug(list[1]?.area || list[0].area);
    }).catch(() => setLoadErr(true));
    api.get("/market/activity").then((r) => setActivity(r.data)).catch(() => setActivity(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload]);

  useEffect(() => {
    if (!urlPair || !areas.length) { return; }
    const parts = urlPair.split("-vs-");
    if (parts.length !== 2) { return; }
    const l = findAreaBySlug(areas, parts[0]);
    const r = findAreaBySlug(areas, parts[1]);
    if (l) setASlug(l.area);
    if (r) setBSlug(r.area);
  }, [urlPair, areas]);

  const syncUrl = (nextA, nextB) => {
    const nextPair = `${toSlug(nextA)}-vs-${toSlug(nextB)}`;
    window.dispatchEvent(new CustomEvent(PAIR_EVENT, { detail: [nextA, nextB] }));
    if (nextPair !== urlPair) {
      window.history.replaceState(null, "", `/dubai-compare-areas/${nextPair}${window.location.search}${window.location.hash}`);
    }
  };

  const A = useMemo(() => areas.find((x) => x.area === aSlug) || areas[0], [areas, aSlug]);
  const B = useMemo(() => areas.find((x) => x.area === bSlug) || areas[1] || areas[0], [areas, bSlug]);
  const t = CT[isAR ? "ar" : "en"];
  if (!areas.length || !A || !B) {
    return (
      <section id="Compare" className="pt-2 sm:pt-4 pb-10 sm:pb-14 relative" data-testid="area-compare-section">
        <div className="mx-auto max-w-[1480px] px-3 sm:px-6 lg:px-8"><Header isAR={isAR} />
          {loadErr ? (
            <div className="rounded-[1.5rem] bg-white/70 border hairline p-8 text-center" data-testid="compare-load-error">
              <p className="text-sm text-propertic-ink-2">{isAR ? "تعذّر تحميل بيانات المناطق حالياً." : "Area data could not be loaded right now."}</p>
              <button type="button" onClick={() => setReload((n) => n + 1)} data-testid="compare-retry-btn" className="mt-4 rounded-full bg-propertic-ink px-5 py-2 text-xs text-white">{isAR ? "إعادة المحاولة" : "Try again"}</button>
            </div>
          ) : <div className="rounded-[1.5rem] bg-white/70 border hairline h-64 animate-pulse" />}
        </div>
      </section>
    );
  }
  const opts = (exclude) => [...areas].filter((x) => x.area !== exclude).sort((a, b) => nm(a, isAR).localeCompare(nm(b, isAR))).map((x) => ({ value: x.area, label: nm(x, isAR) }));
  const asOf = isAR ? arDigits(A.as_of_ar || A.as_of || "") : A.as_of || "";

  return (
    <section id="Compare" className="pt-2 sm:pt-4 pb-10 sm:pb-14 relative" data-testid="area-compare-section">
      {urlPair && !resolvePair(areas, urlPair) && (
        <Helmet>
          <title>{isAR ? "المقارنة غير موجودة · بروبيرتك" : "Comparison not found · The Propertic"}</title>
          <meta name="robots" content="noindex, follow" />
        </Helmet>
      )}
      {urlPair && resolvePair(areas, urlPair) && <PairSeo A={A} B={B} lang={lang} />}
      <div className="mx-auto max-w-[1480px] px-3 sm:px-6 lg:px-8">
        <Header isAR={isAR} />
        <div className="rounded-[1.5rem] border hairline bg-white/80 p-4 sm:p-6 shadow-[0_12px_40px_rgba(10,37,64,0.06)]">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-2 sm:gap-4 items-center">
            <RoundSelect value={A.area} onChange={(v) => { setASlug(v); syncUrl(v, B.area); }} options={opts(B.area)} label={isAR ? "المنطقة الأولى" : "Area A"} testId="compare-a-btn" isAR={isAR} className="w-full" />
            <button type="button" onClick={() => { setASlug(B.area); setBSlug(A.area); syncUrl(B.area, A.area); }} aria-label={isAR ? "تبديل المنطقتين" : "Swap areas"}
              className="justify-self-center w-10 h-10 rounded-full border border-propertic-gold/50 bg-propertic-gold/10 grid place-items-center hover:bg-propertic-gold/20 transition-colors" data-testid="compare-swap-btn">
              <ArrowRightLeft className="w-4 h-4 text-propertic-gold rotate-90 sm:rotate-0" />
            </button>
            <RoundSelect value={B.area} onChange={(v) => { setBSlug(v); syncUrl(A.area, v); }} options={opts(A.area)} label={isAR ? "المنطقة الثانية" : "Area B"} testId="compare-b-btn" isAR={isAR} className="w-full" />
          </div>
          <div className="mt-4"><MetricRows A={A} B={B} isAR={isAR} format={format} /></div>
        </div>
        <div className="mt-6 grid lg:grid-cols-2 gap-6 items-stretch">
          <TrendCompare A={A} B={B} isAR={isAR} />
          <SalesCompare A={A} B={B} activity={activity} isAR={isAR} />
          <BandsCompare A={A} B={B} isAR={isAR} />
          <MovementCompare A={A} B={B} isAR={isAR} />
        </div>
        <p className="mt-4 text-[11px] text-propertic-muted" data-testid="compare-source">
          {t.src}{asOf ? ` · ${isAR ? "آخر تحديث" : "Updated"} ${asOf}` : ""}{(!A.verified || !B.verified) ? ` · ${t.approx}` : ""}
        </p>
      </div>
    </section>
  );
}
